import { z } from 'zod';

/** Canonical language names accepted in CV/profile language fields. */
export const SUPPORTED_CV_LANGUAGE_NAMES = [
  'Afrikaans',
  'Albanian',
  'American Sign Language',
  'Amharic',
  'Arabic',
  'Armenian',
  'Assamese',
  'Azerbaijani',
  'Basque',
  'Belarusian',
  'Bengali',
  'Bosnian',
  'British Sign Language',
  'Bulgarian',
  'Burmese',
  'Cantonese',
  'Catalan',
  'Chinese',
  'Croatian',
  'Czech',
  'Danish',
  'Dari',
  'Dutch',
  'English',
  'Estonian',
  'Farsi',
  'Filipino',
  'Finnish',
  'French',
  'Georgian',
  'German',
  'Greek',
  'Gujarati',
  'Hausa',
  'Hebrew',
  'Hindi',
  'Hungarian',
  'Icelandic',
  'Indonesian',
  'Irish',
  'Italian',
  'Japanese',
  'Kannada',
  'Kazakh',
  'Khmer',
  'Korean',
  'Kurdish',
  'Kyrgyz',
  'Lao',
  'Latin',
  'Latvian',
  'Lithuanian',
  'Macedonian',
  'Malay',
  'Malayalam',
  'Mandarin',
  'Marathi',
  'Mongolian',
  'Nepali',
  'Norwegian',
  'Pashto',
  'Persian',
  'Polish',
  'Portuguese',
  'Punjabi',
  'Romanian',
  'Russian',
  'Serbian',
  'Sign Language',
  'Sinhala',
  'Slovak',
  'Slovenian',
  'Somali',
  'Spanish',
  'Swahili',
  'Swedish',
  'Tagalog',
  'Tamil',
  'Telugu',
  'Thai',
  'Tulu',
  'Turkish',
  'Turkmen',
  'Ukrainian',
  'Urdu',
  'Uzbek',
  'Vietnamese',
  'Welsh',
  'Yoruba',
] as const;

export type SupportedCvLanguageName =
  (typeof SUPPORTED_CV_LANGUAGE_NAMES)[number];

function normalizeLanguageKey(value: string): string {
  return value.normalize('NFKC').replace(/\s+/gu, ' ').trim().toLowerCase();
}

const CANONICAL_LANGUAGE_BY_KEY = new Map<string, SupportedCvLanguageName>(
  SUPPORTED_CV_LANGUAGE_NAMES.map((language) => [
    normalizeLanguageKey(language),
    language,
  ]),
);

const LANGUAGE_ALIASES: Readonly<Record<string, SupportedCvLanguageName>> = {
  bangla: 'Bengali',
  'bahasa indonesia': 'Indonesian',
  castilian: 'Spanish',
  kiswahili: 'Swahili',
  'mandarin chinese': 'Mandarin',
  'modern standard arabic': 'Arabic',
  العربية: 'Arabic',
  'اللغة العربية': 'Arabic',
  الإنجليزية: 'English',
  الانجليزية: 'English',
  'اللغة الإنجليزية': 'English',
  الفرنسية: 'French',
  الإسبانية: 'Spanish',
  الاسبانية: 'Spanish',
  الألمانية: 'German',
  الالمانية: 'German',
  الهندية: 'Hindi',
  الأردية: 'Urdu',
  الاردية: 'Urdu',
};

/** Return a canonical supported name, or undefined for arbitrary input. */
export function canonicalizeCvLanguageName(
  value: unknown,
): SupportedCvLanguageName | undefined {
  if (typeof value !== 'string') return undefined;
  const key = normalizeLanguageKey(value);
  if (!key || key.length > 64) return undefined;

  const direct = CANONICAL_LANGUAGE_BY_KEY.get(key) ?? LANGUAGE_ALIASES[key];
  if (direct) return direct;

  const withoutSuffix = key.replace(/\s+language$/u, '').trim();
  return (
    CANONICAL_LANGUAGE_BY_KEY.get(withoutSuffix) ??
    LANGUAGE_ALIASES[withoutSuffix]
  );
}

export function isSupportedCvLanguageName(value: unknown): boolean {
  return canonicalizeCvLanguageName(value) !== undefined;
}

export const MAX_CV_NAME = 80;
export const MAX_CV_HEADLINE = 120;
export const MAX_CV_BIO = 500;
export const MAX_CV_SUMMARY = 2000;
export const MAX_CV_DESCRIPTION = 1500;
export const MAX_LINK_URL = 500;
export const MAX_CV_SKILL = 60;
export const MAX_CV_LANGUAGE = 60;
export const MAX_CV_SKILL_CATEGORY = 60;
export const MAX_CV_DEGREE = 120;
export const MAX_CV_SCHOOL = 160;
export const MAX_CV_CERT_NAME = 160;
export const MAX_CV_CERT_ISSUER = 160;
export const MAX_CV_CERT_URL = 2048;

export const MIN_EDUCATION_YEAR = 1900;

const MONTH_TOKENS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const YEAR_ONLY = /^\d{4}$/;

export function parseCvPickerDate(value: string): Date | null {
  if (!value) return null;
  const trimmed = value.trim();
  const parts = trimmed.split(/\s+/);
  if (parts.length === 2) {
    const m = MONTH_TOKENS.indexOf(parts[0]);
    if (m < 0 || !YEAR_ONLY.test(parts[1])) return null;
    return new Date(Number(parts[1]), m, 1);
  }
  if (parts.length === 1 && YEAR_ONLY.test(parts[0])) {
    const y = Number(parts[0]);
    if (y > MIN_EDUCATION_YEAR) return new Date(y, 0, 1);
  }
  return null;
}

export function isCvDateInFuture(value: string): boolean {
  const parsed = parseCvPickerDate(value);
  if (!parsed) return false;
  const now = new Date();
  const firstOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return parsed >= firstOfNextMonth;
}

// Case-insensitive month-name → 0-based index lookup (long + short + "sept").
const MONTH_NAME_LOOKUP: Record<string, number> = (() => {
  const map: Record<string, number> = {};
  const long = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ];
  const short = MONTH_TOKENS.map((m) => m.toLowerCase());
  long.forEach((name, idx) => {
    map[name] = idx;
  });
  short.forEach((name, idx) => {
    map[name] = idx;
  });
  map.sept = 8;
  return map;
})();

const MAX_PICKER_YEAR = 2200;

/**
 * Words a CV uses instead of an end date to mean "still there". They carry no
 * concrete date, so they normalize to "" and are reported as `current` by the
 * period parsers rather than being validated as a malformed date.
 */
export const ONGOING_DATE_PATTERN =
  /^(?:present|presently|current|currently|now|ongoing|to[\s-]?date|till[\s-]?date|till[\s-]?now|to[\s-]?present|up\s?to\s?date)$/i;

/** True when `value` is a "still there" marker rather than a real date. */
export function isOngoingDateMarker(value?: string | null): boolean {
  return !!value && ONGOING_DATE_PATTERN.test(value.trim());
}

function yearMonthString(year: number, monthIdx: number | null): string {
  if (
    !Number.isInteger(year) ||
    year < MIN_EDUCATION_YEAR ||
    year > MAX_PICKER_YEAR
  ) {
    return '';
  }
  if (monthIdx == null || monthIdx < 0 || monthIdx > 11) {
    return String(year);
  }
  return `${year}-${String(monthIdx + 1).padStart(2, '0')}`;
}

/**
 * Normalize a loosely-formatted date: as emitted by the AI draft flow or
 * imported from a parsed CV: to the canonical CV storage format: "YYYY-MM",
 * or "YYYY" when only the year is known, or "" when it cannot be parsed.
 *
 * It NEVER returns a day component and NEVER returns the raw string. Anything
 * unrecognized (garbled, out-of-range year, month > 12) collapses to "" so the
 * date picker and validator never render garbage or "Invalid date" from an
 * AI-supplied value the manual UI could never produce (issues #175/#176/#178/
 * #181). Day-inclusive shapes ("MM/DD/YYYY", ISO "YYYY-MM-DD") are accepted but
 * the day is dropped to match the month/year-only UI.
 *
 * Accepts: "YYYY", "YYYY-MM", ISO "YYYY-MM-DD[THH:…]", "YYYY/MM", "MM-YYYY",
 * "MM/YYYY", "MM/DD/YYYY", "MM-DD-YYYY", "DD/MM/YYYY" (when the first field is
 * not a valid month), "Sep 2022", "Sept 2022", and "September 2022".
 */
export function normalizeToYearMonth(value?: string | null): string {
  if (!value) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  // Ongoing / open-ended markers carry no concrete date.
  if (ONGOING_DATE_PATTERN.test(raw)) return '';

  // Named month + year: "September 2022" / "Sep 2022" / "Sept. 2022" and the
  // punctuated shapes CVs print in date columns ("Mar-2015", "Mar/2015",
  // "Mar.2015"). Without the separator class these collapsed to "" and the
  // imported entry looked like it had no date at all.
  const named = raw.match(/^([A-Za-z]{3,9})\s*[.\-/]?\s*(\d{4})$/);
  if (named) {
    const idx = MONTH_NAME_LOOKUP[named[1].toLowerCase()];
    return idx === undefined ? '' : yearMonthString(Number(named[2]), idx);
  }

  // Year-first with dashes: "2022", "2022-09", ISO "2022-09-15T00:00:00Z".
  const iso = raw.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?(?:[T ].*)?$/);
  if (iso) return yearMonthString(Number(iso[1]), Number(iso[2]) - 1);

  // Year-first with slash: "2022/09".
  const ymSlash = raw.match(/^(\d{4})\/(\d{1,2})$/);
  if (ymSlash)
    return yearMonthString(Number(ymSlash[1]), Number(ymSlash[2]) - 1);

  // Three-part day-inclusive: US "MM/DD/YYYY" or EU "DD/MM/YYYY" (slash or dash).
  const dmy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmy) {
    const a = Number(dmy[1]);
    const b = Number(dmy[2]);
    // Prefer MM/DD; fall back to DD/MM when the first field can't be a month.
    const monthIdx =
      a >= 1 && a <= 12 ? a - 1 : b >= 1 && b <= 12 ? b - 1 : null;
    return yearMonthString(Number(dmy[3]), monthIdx);
  }

  // Two-part month-year: "09-2022", "9/2022".
  const my = raw.match(/^(\d{1,2})[/-](\d{4})$/);
  if (my) {
    const m = Number(my[1]);
    return yearMonthString(Number(my[2]), m >= 1 && m <= 12 ? m - 1 : null);
  }

  // Bare year.
  if (/^\d{4}$/.test(raw)) return yearMonthString(Number(raw), null);

  return '';
}

/**
 * Convert any loosely-formatted date to the CV date picker's DISPLAY format
 * ("Jan 2023", or "2023" when only the year is known). Anything unreadable
 * becomes "" rather than leaking through as text the picker would render as
 * "Invalid date".
 */
export function formatYearMonthForPicker(value?: string | null): string {
  const normalized = normalizeToYearMonth(value);
  if (!normalized) return '';
  const ym = normalized.match(/^(\d{4})-(\d{2})$/);
  if (!ym) return normalized;
  return `${MONTH_TOKENS[Number(ym[2]) - 1]} ${ym[1]}`;
}

// Range separators between the two dates of a period. An ASCII hyphen only
// separates when it has whitespace on one side, so a punctuated single date
// such as "Mar-2015" is never torn in half; the typographic dashes never occur
// inside a date token, so they always separate.
const SPACED_RANGE_SEPARATOR =
  /\s+[-\u2010-\u2015\u2212]\s*|\s*[-\u2010-\u2015\u2212]\s+|\s+(?:to|until|till)\s+/i;
// "2019-2021" / "2019–2021": no whitespace at all, so the rule above cannot
// fire, yet two bare years around a dash are unambiguously a range.
const BARE_YEAR_RANGE = /^(\d{4})\s*[-\u2010-\u2015\u2212]\s*(\d{4})$/;

/**
 * Split a CV period string into its two halves and an "still there" flag.
 *
 * Handles the notations imported CVs and older stored profiles actually use:
 * "Jan 2020 - Present", "Jan 2020 – Present" (en dash), an em-dash range,
 * "2019 to 2021", "2019-2021", "Mar-2015 - Dec-2015", and a bare "Present".
 * Neither half is date-validated here: callers decide what an unreadable half
 * means.
 */
export function splitCvPeriodRange(period?: string | null): {
  start: string;
  end: string;
  current: boolean;
} {
  const trimmed = (period ?? '').trim();
  if (!trimmed) return { start: '', end: '', current: false };
  if (isOngoingDateMarker(trimmed)) {
    return { start: '', end: '', current: true };
  }

  // A known end with an unknown start must not be reinterpreted as a start.
  const endOnly = trimmed.match(/^[-–—]\s+(.+)$/);
  if (endOnly) return { start: '', end: endOnly[1].trim(), current: false };
  const bareYears = trimmed.match(BARE_YEAR_RANGE);
  const [start, end] = bareYears
    ? [bareYears[1], bareYears[2]]
    : splitOnSeparator(trimmed);

  if (isOngoingDateMarker(end)) return { start, end: '', current: true };
  return { start, end, current: false };
}

function splitOnSeparator(period: string): [string, string] {
  const parts = period.split(SPACED_RANGE_SEPARATOR);
  if (parts.length < 2) return [period, ''];
  // Rejoin any surplus splits so a three-token period keeps its tail on the end.
  return [parts[0].trim(), parts.slice(1).join(' - ').trim()];
}

const EXECUTABLE_HTML_BLOCK_PATTERN =
  /<\s*(script|style|iframe|object|embed|template|noscript|svg|math)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi;
const HTML_COMMENT_PATTERN = /<!--[\s\S]*?-->/g;
const HTML_BLOCK_BREAK_PATTERN =
  /<\s*(?:br|hr)\b[^>]*\/?\s*>|<\s*\/\s*(?:p|div|li|h[1-6]|section|article|tr)\s*>/gi;
const HTML_TAG_PATTERN = /<\s*\/?\s*[a-z][^<>]*>/gi;
const HTML_DECLARATION_PATTERN = /<\s*[!?][^>]*>/g;

/**
 * Convert untrusted HTML-like input into plain text.
 *
 * Executable/container elements are removed with their contents. Ordinary
 * formatting tags are stripped while their visible text is retained. Encoded
 * markup such as `&lt;script&gt;` stays encoded and therefore remains inert.
 * This helper is platform-neutral so web, native, and API write boundaries can
 * apply the same rule before CV text is stored or rendered.
 */
export function stripHtmlFromPlainText(value: string): string {
  let result = value.replace(/\u0000/g, '');

  // Repeat the complete strip pass so nested/malformed markup cannot reveal a
  // new executable tag after an inner tag is removed (mutation-XSS shape).
  // The bound prevents hostile input from creating an unbounded loop.
  for (let pass = 0; pass < 8; pass += 1) {
    const next = result
      .replace(EXECUTABLE_HTML_BLOCK_PATTERN, ' ')
      .replace(HTML_COMMENT_PATTERN, ' ')
      .replace(HTML_BLOCK_BREAK_PATTERN, '\n')
      .replace(HTML_TAG_PATTERN, ' ')
      .replace(HTML_DECLARATION_PATTERN, ' ');
    if (next === result) break;
    result = next;
  }

  return result
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\s+([,.;:!?،؛؟])/g, '$1')
    .trim();
}

// Issue #34: shared guard for short "name" fields (job title, company, school,
// degree, certification name). Real names/roles use a wide range of punctuation
// (e.g. "AT&T", "C++", "R&D / QA", "@home"), so instead of a strict
// allow-list we DENY only characters that signal markup / injection and never
// appear in a legitimate name: < > { } [ ] \ ` ~ ^ ; *. Everything else -
// letters of any script, digits, whitespace, and common punctuation incl.
// @ + - & ' . , / | ( ) # : % =: is allowed. Pipes are common bilingual
// separators (for example, English | Arabic) and are data here, not shell
// operators. Contextual checks below still
// reject SQL-style boolean/equality expressions without breaking legitimate
// names such as "O'Reilly Media", "AT&T", or "C++ Labs". (Real injection
// safety remains the server's parameterized queries; this guard keeps obvious
// garbage out of CV fields.) Empty strings pass; required-ness is enforced
// separately so the "remove special characters" message never masks a
// "required" one.
export const SAFE_NAME_PATTERN = /^[^<>{}\[\]\\`~^;*]*$/u;

const SQL_BOOLEAN_EXPRESSION_PATTERN =
  /\b(?:or|and)\b\s+(?:['"]?[\p{L}\p{N}_-]+['"]?)\s*=\s*(?:['"]?[\p{L}\p{N}_-]+['"]?)/iu;
const QUOTED_EQUALITY_PATTERN =
  /['"][^'"\r\n]{0,64}['"]\s*=\s*['"][^'"\r\n]{0,64}['"]/u;

export function isValidNameField(value: string): boolean {
  return (
    SAFE_NAME_PATTERN.test(value) &&
    !SQL_BOOLEAN_EXPRESSION_PATTERN.test(value) &&
    !QUOTED_EQUALITY_PATTERN.test(value)
  );
}

// Returns the i18n message key when `value` contains disallowed characters,
// otherwise undefined. Mirrors the schema-message-as-i18n-key convention so
// callers translate it the same way (`t('cv.edit.invalidChars')`).
export function nameFieldError(value: string): string | undefined {
  return isValidNameField(value) ? undefined : 'cv.edit.invalidChars';
}

// Schema messages use i18n keys (e.g. 'cv.edit.tooLong'). Callers translate
// via `t()`. For parameterized messages like `tooLong`, the caller supplies
// the `{ max }` interpolation based on the field's known limit.

export const cvHeadlineSchema = z
  .string()
  .trim()
  .min(1, 'cv.edit.headlineRequired')
  .max(MAX_CV_HEADLINE, 'cv.edit.tooLong');

export const cvSummarySchema = z
  .string()
  .trim()
  .min(1, 'cv.edit.summaryRequired')
  .max(MAX_CV_SUMMARY, 'cv.edit.tooLong');

export const cvDescriptionSchema = z
  .string()
  .trim()
  .max(MAX_CV_DESCRIPTION, 'cv.edit.tooLong');

const currentYear = () => new Date().getFullYear();

export const cvEducationSchema = z.object({
  degree: z
    .string()
    .trim()
    .min(1, 'cv.edit.degreeRequired')
    .max(MAX_CV_DEGREE, 'cv.edit.tooLong')
    .refine(isValidNameField, 'cv.edit.invalidChars'),
  school: z
    .string()
    .trim()
    .min(1, 'cv.edit.schoolRequired')
    .max(MAX_CV_SCHOOL, 'cv.edit.tooLong')
    .refine(isValidNameField, 'cv.edit.invalidChars'),
  // Optional. Plenty of CVs list a school and degree with no graduation year,
  // and a bulk import must not dead-end the onboarding step on a date the
  // source document never contained. A year that IS present is still checked.
  year: z
    .string()
    .trim()
    .superRefine((value, ctx) => {
      if (!value) return;
      const parsed = parseCvPickerDate(value);
      if (!parsed) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'cv.edit.invalidYear',
        });
        return;
      }
      const y = parsed.getFullYear();
      if (y < MIN_EDUCATION_YEAR || y > currentYear() + 10) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'cv.edit.invalidYear',
        });
      }
    }),
  description: z
    .string()
    .trim()
    .max(MAX_CV_DESCRIPTION, 'cv.edit.tooLong')
    .optional()
    .or(z.literal('')),
});

export const cvCertificationSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'cv.edit.nameRequired')
    .max(MAX_CV_CERT_NAME, 'cv.edit.tooLong')
    .regex(SAFE_NAME_PATTERN, 'cv.edit.invalidChars'),
  issuer: z
    .string()
    .trim()
    .min(1, 'cv.edit.issuerRequired')
    .max(MAX_CV_CERT_ISSUER, 'cv.edit.tooLong'),
  date: z
    .string()
    .trim()
    .superRefine((value, ctx) => {
      if (!value) return;
      const parsed = parseCvPickerDate(value);
      if (!parsed) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'cv.edit.invalidDate',
        });
        return;
      }
      if (isCvDateInFuture(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'cv.edit.dateInFuture',
        });
      }
    }),
  // Optional verifiable credential link (e.g. Credly/AWS badge URL). Empty is
  // allowed. The scheme is optional here ("credly.com/badges/abc" is accepted)
  // and normalized to https:// on save via `normalizeCredentialUrl`; we only
  // require something that looks like a domain (a dot, no whitespace).
  credentialUrl: z
    .string()
    .trim()
    .max(MAX_CV_CERT_URL, 'cv.edit.tooLong')
    .optional()
    .superRefine((value, ctx) => {
      if (value && !/^(https?:\/\/)?[^\s]+\.[^\s]+$/i.test(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'cv.edit.invalidUrl',
        });
      }
    }),
});

/**
 * Normalize a user-entered credential link for storage. Trims, drops empties,
 * and prepends `https://` when the user omitted the scheme (e.g. pasted
 * "credly.com/badges/abc") so the stored value is always a resolvable URL.
 */
export function normalizeCredentialUrl(
  value?: string | null,
): string | undefined {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return undefined;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

export const cvSkillEntrySchema = z
  .string()
  .trim()
  .min(1, 'cv.edit.skillRequired')
  .max(MAX_CV_SKILL, 'cv.edit.tooLong');

export const cvSkillCategorySchema = z
  .string()
  .trim()
  .min(1, 'cv.edit.categoryRequired')
  .max(MAX_CV_SKILL_CATEGORY, 'cv.edit.tooLong');

export const cvLanguageEntrySchema = z
  .string()
  .trim()
  .min(1, 'cv.edit.languageRequired')
  .max(MAX_CV_LANGUAGE, 'cv.edit.tooLong')
  .superRefine((value, ctx) => {
    if (value && !canonicalizeCvLanguageName(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'cv.edit.unsupportedLanguage',
      });
    }
  })
  .transform((value) => canonicalizeCvLanguageName(value) ?? value);

export const cvHighlightSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'cv.edit.highlightTitleRequired')
    .max(MAX_CV_HEADLINE, 'cv.edit.tooLong'),
  desc: z
    .string()
    .trim()
    .min(1, 'cv.edit.highlightDescRequired')
    .max(MAX_CV_DESCRIPTION, 'cv.edit.tooLong'),
});

// Max length lookup used by validator wrappers to interpolate `{ max }` into
// the shared `cv.edit.tooLong` translation. Keyed by field path segments
// (e.g. 'degree', 'school', 'summary') to match ZodIssue.path.
export const CV_FIELD_MAX: Record<string, number> = {
  headline: MAX_CV_HEADLINE,
  summary: MAX_CV_SUMMARY,
  description: MAX_CV_DESCRIPTION,
  degree: MAX_CV_DEGREE,
  school: MAX_CV_SCHOOL,
  name: MAX_CV_CERT_NAME,
  issuer: MAX_CV_CERT_ISSUER,
  skill: MAX_CV_SKILL,
  category: MAX_CV_SKILL_CATEGORY,
  language: MAX_CV_LANGUAGE,
  url: MAX_LINK_URL,
  title: MAX_CV_HEADLINE,
  desc: MAX_CV_DESCRIPTION,
};
